import axios from 'axios';
import { usePage } from '@inertiajs/vue3';

/**
 * Secure Axios Composable
 *
 * Provides axios wrappers that automatically include company context validation token
 * This prevents cross-tenant data contamination when using axios directly
 *
 * Usage:
 * Instead of using axios directly, use the wrappers from this composable:
 *
 * Before:
 *   await axios.post('/work-orders/123/assign', { user_id: 5 });
 *
 * After:
 *   const { securePost } = useSecureAxios();
 *   await securePost('/work-orders/123/assign', { user_id: 5 });
 *
 * The _company_id is automatically included in the request body.
 */
export function useSecureAxios() {
    const page = usePage();

    /**
     * Get the current company ID from page props
     */
    const getCompanyId = () => {
        return page.props.company_id;
    };

    /**
     * Merge company ID with request data
     * Handles both plain objects and FormData
     */
    const mergeCompanyId = (data = {}) => {
        // If data is FormData, append company_id to it
        if (data instanceof FormData) {
            data.append('_company_id', getCompanyId());
            return data;
        }

        // Otherwise, spread the object
        return {
            ...data,
            _company_id: getCompanyId(),
        };
    };

    /**
     * Secure POST request
     */
    const securePost = async (url, data = {}, config = {}) => {
        return axios.post(url, mergeCompanyId(data), config);
    };

    /**
     * Secure PUT request
     */
    const securePut = async (url, data = {}, config = {}) => {
        return axios.put(url, mergeCompanyId(data), config);
    };

    /**
     * Secure PATCH request
     */
    const securePatch = async (url, data = {}, config = {}) => {
        return axios.patch(url, mergeCompanyId(data), config);
    };

    /**
     * Secure DELETE request
     * Note: DELETE requests typically use request body, not query params
     */
    const secureDelete = async (url, config = {}) => {
        return axios.delete(url, {
            ...config,
            data: mergeCompanyId(config.data || {}),
        });
    };

    /**
     * Regular GET request (no company ID needed for reads)
     */
    const get = async (url, config = {}) => {
        return axios.get(url, config);
    };

    return {
        securePost,
        securePut,
        securePatch,
        secureDelete,
        get,
        // Also export the individual methods with shorter names
        post: securePost,
        put: securePut,
        patch: securePatch,
        delete: secureDelete,
    };
}
